import * as _ from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState, type VoidFunctionComponent } from 'react';
import { HelpBlock, InputGroup, Modal } from 'react-bootstrap';
import type { UseQueryResult } from '@tanstack/react-query';
import type { SetRequired } from 'sequelize/types/utils/set-required';

import {
  type NoticeMatch,
  type NoticeMatchCorrectionRequest,
  NoticeMatchCorrectionType,
} from '../../../../shared/types/routes/revisions';
import { Action } from '../../../../typings/permissions';
import { useDialogContext } from '../../../contexts/DialogContext';
import { useAddToast } from '../../../contexts/ToastContext';
import { window } from '../../../globals';
import { userHasPermissionForProject } from '../../../helpers/permissions';
import useProjectCorrection from '../../../hooks/api/projects/useProjectCorrection';
import useNoticeFiles from '../../../hooks/api/revisions/useNoticeFiles';
import useRevision from '../../../hooks/api/revisions/useRevision';
import { isErrorResponse } from '../../../ReactQuery/errors';
import { verify_subscription } from '../../../utils';
import Button from '../../generic/Button/Button';
import ButtonGroup from '../../generic/Button/ButtonGroup';
import TooltipButton from '../../generic/Button/TooltipButton';
import FormGroup from '../../generic/Forms/FormGroup';
import LabeledMultiTextInput from '../../generic/Forms/LabeledMultiTextInput';
import LabeledTextArea from '../../generic/Forms/LabeledTextArea';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import { icons } from '../../generic/Icon/icons';
import { Icon } from '../../generic/Icon/index';
import Loader from '../../generic/Loader';
import LicenseEditableList from '../../licenses/EditableList';
import { getLicenseGroupsFromRevision } from '../../licenses/groups';
import type { UpdateProjectCorrectionRequestLicense } from '../../../../shared/types/licenses';
import type { UpdateProjectCorrectionRequestProject } from '../../../../typings/api';
import type { Project, Revision } from '../../../types';
import type { TextInputOnChange } from '../../generic/Forms/InputBase';

import NoticeFiles from './NoticeFiles';

import styles from './ModalEditDependency.module.less';

type ModalProps = {
  // The locator of the dependency revision to be edited.
  // Some pages that include this modal do not know what revision will be edited when the modal is first rendered. If no
  // `revisionLocator` is provided then the modal will not show.
  revisionLocator?: string;
  // The locator of the user revision that imports the dependency.
  // If provided, then an issue scan will be performed after any changes are made.
  parentRevisionLocator?: string;

  // Modal state management
  show: boolean;
  onHide(): void;

  // An optional callback that the caller can provide that will be triggered when a project correction is upserted or
  // deleted. Use this so that your component can trigger a data refresh, etc.
  onChange?(): void;
};

type LoadProps = Omit<SetRequired<ModalProps, 'revisionLocator'>, 'show'>;

type EditProps = {
  // The revision object of the dependency revision to be edited.
  revision: Revision & { project: Project };
  notices: UseQueryResult<NoticeMatch[], Error>;
} & Omit<ModalProps, 'revisionLocator' | 'show'>;

const buildNoticeMatchCorrection = (
  ogNotice: NoticeMatch | undefined,
  modifiedNotice: NoticeMatch,
): NoticeMatchCorrectionRequest | undefined => {
  // In the future this codepath will likely be used to add a new NOTICE file.
  // But the corrections backend does not currently support that.
  if (!ogNotice) {
    return undefined;
  }

  const baseCorrectionRequest = {
    path: ogNotice.path,
  };

  if (ogNotice.ignored !== modifiedNotice.ignored) {
    return {
      ...baseCorrectionRequest,
      correctionType: modifiedNotice.ignored
        ? NoticeMatchCorrectionType.Ignored : NoticeMatchCorrectionType.RemoveIgnored,
    };
  }

  const ogCopyrights = structuredClone(ogNotice.copyrights || []).sort();
  const currentCopyrights = structuredClone(modifiedNotice.copyrights || []).sort();
  const haveCopyrightsChanged = !_.isEqual(ogCopyrights, currentCopyrights);

  const haveContentsChanged = ogNotice.contents !== modifiedNotice.contents;

  if (!haveCopyrightsChanged && !haveContentsChanged) {
    return undefined;
  }

  return {
    ...baseCorrectionRequest,
    correctionType: NoticeMatchCorrectionType.Edited,
    copyrights: haveCopyrightsChanged ? modifiedNotice.copyrights : undefined,
    contents: haveContentsChanged ? modifiedNotice.contents : undefined,
  };
};

const EditDependency: VoidFunctionComponent<EditProps> = ({
  revision, notices, parentRevisionLocator, onHide, onChange,
}) => {
  const dialogContext = useDialogContext();
  const addToast = useAddToast();
  const [isRebuilding, setIsRebuilding] = useState(false);
  const { upsertCorrection, deleteCorrection, rebuild } = useProjectCorrection(
    revision.locator, parentRevisionLocator,
  );

  const initialState = useMemo(() => ({
    title: revision.project.title,
    url: revision.project.url || '',
    description: revision.project.description || '',
    authors: revision.project.authors || [],
    licenseGroups: getLicenseGroupsFromRevision(revision),
    noticeFiles: _.keyBy(notices.data, n => n.path),
  }), [revision, notices.data]);
  const [values, setValues] = useState(initialState);
  useEffect(() => setValues(initialState), [initialState]);
  const [modifiedNoticeFiles, setModifiedNoticeFiles] = useState<Record<string, NoticeMatchCorrectionRequest>>({});
  const handleInputChange: TextInputOnChange = (_value, event) => {
    if (!event) {
      return;
    }

    const { name, value } = event.target;
    setValues({
      ...values,
      [name]: value,
    });
  };
  const handleNoticeFileChange = (change: NoticeMatch) => {
    setValues({
      ...values,
      noticeFiles: {
        ...values.noticeFiles,
        [change.path]: change,
      },
    });

    const correction = buildNoticeMatchCorrection(initialState.noticeFiles[change.path], change);
    if (!correction) {
      const newState = _.omit(modifiedNoticeFiles, [change.path]);
      setModifiedNoticeFiles(newState);
    } else {
      setModifiedNoticeFiles({
        ...modifiedNoticeFiles,
        [change.path]: correction,
      });
    }
  };

  const hasCorrection = (revision as { corrected?: boolean }).corrected || !!revision.project.projectCorrection;
  const canEdit = userHasPermissionForProject(Action.Edit, revision.projectId);
  const canRebuild = revision.project.organizationId ? canEdit : true;

  const getChangedFields = () => {
    const changes: Record<keyof typeof initialState, boolean> = {
      title: initialState.title !== values.title,
      url: initialState.url !== values.url,
      description: initialState.description !== values.description,
      authors: !_.isEqual(initialState.authors.toSorted(), values.authors.toSorted()),
      licenseGroups: !_.isEqual(
        initialState.licenseGroups.map(lg => lg.licenses),
        values.licenseGroups.map(lg => lg.licenses),
      ),
      noticeFiles: Object.keys(modifiedNoticeFiles).length > 0,
    };
    return Object.keys(_.pickBy(changes));
  };

  const onClickDelete = () => {
    dialogContext.onConfirm({
      title: 'Delete project correction',
      body: <p>Are you sure you want to delete this correction?</p>,
      onConfirm() {
        deleteCorrection.mutate(undefined, {
          onSuccess() {
            addToast({
              type: 'success',
              content: 'Project correction has been deleted',
            });
            onHide();
            onChange?.();
          },
          onError() {
            addToast({
              type: 'error',
              content: `Unable to delete project correction`,
            });
          },
        });
      },
    });
  };

  const onClickSave = () => {
    if (!verify_subscription('premium')) {
      window.showPlanSelectorModal();
      return;
    }

    const project = _.omit(values, ['licenseGroups', 'noticeFiles']);
    const licenses = values.licenseGroups.map(lg => lg.licenses) as any as UpdateProjectCorrectionRequestLicense[][];
    upsertCorrection.mutate({
      project: project as UpdateProjectCorrectionRequestProject,
      licenses,
      noticeFiles: Object.values(modifiedNoticeFiles),
    }, {
      onSuccess() {
        addToast({
          type: 'success',
          content: 'Project correction has been saved',
        });
        onHide();
        onChange?.();
      },
      onError() {
        addToast({
          type: 'error',
          content: `Unable to save project correction`,
        });
      },
    });
  };

  return (
    // This modal MUST NOT HIDE unless the user explicitly hits the "Cancel" button.
    // Kate Downing gets incredibly aggravated when she accidentally loses her work in progress.
    // `backdrop="static"` disables the hide-on-backdrop-click
    // `keyboard={false}` disables the hide-on-esc-key
    // See CORE-4372 for more information.
    <Modal show onHide={onHide} animation backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <div className={styles.header}>
          <div>
            <Modal.Title>Edit Dependency</Modal.Title>
            <span>{revision.project.title}</span>
          </div>

          <TooltipButton
            id="reanalyze-button-tooltip"
            // If the value is null, we want to hide the timestamp.
            showTooltip={!!revision.dependency_cache_updated}
            // Show the exact timestamp to super users for debugging purposes, since it's not shown anywhere else in the
            // UI.
            tooltip={`Last analyzed: ${
              window.user?.super
                ? revision.dependency_cache_updated
                : moment(revision.dependency_cache_updated).fromNow()
            }`}
            type="secondary"
            size="large"
            isDisabled={isRebuilding || !canRebuild}
            onClick={() => {
              rebuild.mutate();
              setIsRebuilding(true);
            }}
          >
            {isRebuilding ? <Icon icon={icons.circleO} hasSpin /> : <Icon icon={icons.reload} />}{' '}
            Reanalyze
          </TooltipButton>
        </div>
      </Modal.Header>

      <Modal.Body>
        <FormGroup>
          <LabeledTextInput
            labelVariant="legacy"
            label="Name"
            name="title"
            size="large"
            value={values.title}
            onChange={handleInputChange}
          />
          <LabeledTextInput
            labelVariant="legacy"
            label="Homepage"
            name="url"
            size="large"
            value={values.url}
            onChange={handleInputChange}
          />
          <LabeledTextArea
            labelVariant="legacy"
            label="Description"
            name="description"
            size="large"
            value={values.description}
            onChange={handleInputChange}
          />
          <div className={styles.licenseList}>
            <LicenseEditableList
              onChange={(licenseGroups => setValues({ ...values, licenseGroups }))}
              revision={revision}
              canEdit={canEdit}
              shouldSkipWarning
            />
          </div>
          <NoticeFiles
            isLoading={notices.isLoading}
            isError={notices.isError}
            notices={values.noticeFiles}
            onChange={handleNoticeFileChange}
          />
          <div className={styles.inputGroup}>
            <InputGroup>
              <LabeledMultiTextInput
                labelVariant="legacy"
                id="authors"
                name="authors"
                label="Authors"
                size="large"
                values={values.authors}
                onChange={(authors) => setValues({ ...values, authors })}
                shouldHaveMaxHeight
          />
              <HelpBlock>Press enter after entering each author.</HelpBlock>
            </InputGroup>
          </div>
        </FormGroup>
      </Modal.Body>

      <Modal.Footer>
        <ButtonGroup>
          <Button type="secondary" size="medium" onClick={onHide}>Cancel</Button>
          {canEdit && hasCorrection && (
            <Button
              type="error"
              size="medium"
              isDisabled={deleteCorrection.isLoading}
              onClick={onClickDelete}
            >
              Delete Correction
            </Button>
          )}
          {canEdit && (
            <Button
              type="primary"
              size="medium"
              isDisabled={getChangedFields().length === 0 || upsertCorrection.isLoading}
              onClick={onClickSave}
            >
              <Icon icon={icons.save} /> Save Changes
            </Button>
          )}
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Shown while the request to the revisions API is pending.
 */
const Loading: VoidFunctionComponent<{ onHide: () => void }> = ({ onHide }) => (
  <Modal show onHide={onHide}>
    <Modal.Header closeButton>
      Loading...
    </Modal.Header>
    <Modal.Body><Loader /></Modal.Body>
  </Modal>
);

/**
 * Shown when the revisions API returns a non-200 status code.
 */
const GenericError: VoidFunctionComponent<{ onHide: () => void }> = ({ onHide }) => (
  <Modal show onHide={onHide}>
    <Modal.Header closeButton>
      <Modal.Title>Uh oh</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>FOSSA encountered an error while trying to edit this dependency. Please try again.</p>
    </Modal.Body>
  </Modal>
);

/**
 * Shown when the revisions API returns a 404, which should only happen if the locator belongs to an unknown dependency or the user does not have permission to edit it.
 */
const NotFoundError: VoidFunctionComponent<{ onHide: () => void }> = ({ onHide }) => (
  <Modal show onHide={onHide}>
    <Modal.Header closeButton>
      <Modal.Title>Sorry</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        This dependency could not be found or you don&apos;t have permission to edit it. If you believe this is
        incorrect, please contact support for assistance.
      </p>
    </Modal.Body>
  </Modal>
);

const LoadDependency: VoidFunctionComponent<LoadProps> = ({
  revisionLocator, parentRevisionLocator, onHide, onChange,
}) => {
  const revision = useRevision(revisionLocator, true);
  const notices = useNoticeFiles(revisionLocator);

  if (revision.isLoading) {
    return <Loading onHide={onHide} />;
  }

  if (revision.isError) {
    // If a user tries to edit an unknown dependency then the API will return a 404 status code.
    if (isErrorResponse(revision.error) && revision.error.status === 404) {
      return <NotFoundError onHide={onHide} />;
    }
    return <GenericError onHide={onHide} />;
  }

  return (
    <EditDependency
      revision={revision.data as any}
      notices={notices}
      parentRevisionLocator={parentRevisionLocator}
      onHide={onHide}
      onChange={onChange}
    />
  );
};

const ModalEditDependency: VoidFunctionComponent<ModalProps> = ({
  revisionLocator, parentRevisionLocator, show, onHide, onChange,
}) => {
  if (!show || !revisionLocator) {
    return null;
  }

  return (<LoadDependency
    revisionLocator={revisionLocator}
    parentRevisionLocator={parentRevisionLocator}
    onHide={onHide}
    onChange={onChange}
  />);
};

export default ModalEditDependency;
